/**
 * @file src/ui/react/studio/useOfflineDownloadIntent.ts
 * @description Act on `?offline=download` once, when the collection is actually here.
 *
 * Root hands MultiStage the intent; this is the one place it is honoured. It waits for two things
 * before doing anything — the roster (a download resolves from the drawer's cards, and an empty
 * roster downloads nothing and reports success) and the store's first read (before it, "never
 * downloaded" and "not looked yet" are the same null). Then it fires exactly once, removes the
 * intent from the address, and hands the rest to the same `download` / `resume` the control's
 * buttons call — so the progress, the cap refusal and the errors all surface where they already do.
 *
 * A collection that is already stored is left alone. Asking again for something that is here is not
 * a request to fetch it twice.
 */
import { useEffect, useRef } from 'react';
import {
  clearOfflineDownloadIntent,
  hasOfflineDownloadIntent,
  offlineDownloadQuality,
} from '../../../offline/offlineIntent.js';
import type { UseOfflineCollectionResult } from './useOfflineCollection';

export function useOfflineDownloadIntent({
  offline,
  ready,
  allowed,
}: {
  offline: UseOfflineCollectionResult;
  /** The roster is on screen and the store has been read. */
  ready: boolean;
  /** The download control would be offered here at all (desktop, not in a room). */
  allowed: boolean;
}): void {
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    if (!offline.enabled || !ready || !allowed) return;
    if (!hasOfflineDownloadIntent()) return;
    const wanted = offlineDownloadQuality();
    consumed.current = true;
    clearOfflineDownloadIntent();

    const status = offline.status;
    if (status?.running || offline.busy) return;
    // Already here is already here. Root asks which quality BEFORE sending anybody this way, so a
    // collection that is stored is not a request to fetch it again at the other one — changing that
    // is remove-and-download, which asks the question again on the way through.
    if (status?.state === 'ready' && !status.evicted) return;
    // Partly here (or reclaimed by the browser): finish it rather than start over. The pinned
    // versions come from the manifest, so this needs no roster.
    if (status && (status.state === 'partial' || status.evicted)) {
      void offline.resume();
      return;
    }
    void offline.download(wanted);
  }, [allowed, offline, ready]);
}
